// Shared non-component helpers for the applicant pipeline card/row/drawer
// set (Prompt 138W-R4/R5, Prompt 138Y-B/C/D). Factored out of
// ApplicantCard.jsx so that file can stay component-only
// (react-refresh/only-export-components).

// Prompt 139C: the source Invite Card's own effective status
// (WorkerApplicationInviteCardDto.sourceStatus - Active/Revoked/Expired/
// Draft), rendered as a calm, factual note - never a reason to hide the
// applicant. Returns null for "Active" (or when unknown) since that is the
// unremarkable default case with nothing to call out; only a non-Active
// source gets a visible note, and even then it explicitly reassures the
// reader the applicant itself is unaffected.
export function describeInviteSourceStatus(sourceStatus) {
  switch (sourceStatus) {
    case "Revoked":
      return "ลิงก์ Invite Card นี้ถูกเพิกถอนแล้ว (ใบสมัครนี้ยังอยู่ในระบบตามปกติ)";
    case "Expired":
      return "ลิงก์ Invite Card นี้หมดอายุแล้ว (ใบสมัครนี้ยังอยู่ในระบบตามปกติ)";
    case "Draft":
      return "ลิงก์ Invite Card นี้ยังไม่เปิดใช้งาน (ใบสมัครนี้ยังอยู่ในระบบตามปกติ)";
    default:
      return null;
  }
}

export function formatDateTh(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// Prompt 138W-R5: every real value dbo.WmxWorkerApplications.ApplicationStatus
// can hold (Services/Public/PublicWorkerApplicationRecords.cs's own
// ApplicationStatuses constants) - QuickRegistered (Prompt 091), the four
// EkycXxx values StartEkycAsync/MakeDecisionAsync write (Prompt 095), and
// WorkerProfileCreated (Prompt 097). Read directly from applicationStatus,
// never from the list/detail endpoints' own `stage.code` field - that field
// is still hardcoded to "AwaitingEkyc" in WorkerApplicationService.ToListItemDto
// regardless of the real ApplicationStatus (a pre-existing backend
// simplification from before Prompt 095 existed), so it goes stale the
// moment e-KYC actually starts. applicationStatus is the one field that is
// always accurate.
const EKYC_STATUS_META = {
  QuickRegistered: { label: "รอ e-KYC", badgeVariant: "warning" },
  EkycInProgress: { label: "e-KYC กำลังดำเนินการ", badgeVariant: "info" },
  EkycNeedsCorrection: { label: "e-KYC ต้องแก้ไขข้อมูล", badgeVariant: "warning" },
  EkycApproved: { label: "e-KYC ผ่านแล้ว", badgeVariant: "success" },
  EkycRejected: { label: "e-KYC ไม่ผ่าน", badgeVariant: "warning" },
  WorkerProfileCreated: { label: "สร้าง Worker Profile แล้ว", badgeVariant: "success" },
};
const DEFAULT_STATUS_META = { label: "รอ e-KYC", badgeVariant: "warning" };

/** Real-status badge text/variant - amber/blue/green only, never red (a pipeline candidate is never shown as an error state). */
export function describeApplicationStatus(applicationStatus) {
  return EKYC_STATUS_META[applicationStatus] ?? DEFAULT_STATUS_META;
}

// "attention" (amber) is deliberately distinct from "done" (green) - a
// rejected or needs-correction review is a real terminal/blocked state, not
// a success, so it must never render with the same green dot an Approved
// review gets even though both are past "waiting"/"active".
const EKYC_STEP_STATE = {
  QuickRegistered: "waiting",
  EkycInProgress: "active",
  EkycNeedsCorrection: "attention",
  EkycApproved: "done",
  EkycRejected: "attention",
  WorkerProfileCreated: "done",
};

// Prompt 138Y-B, wording finalized by Prompt 138Y-D: the longer,
// "this is a central-process step" framing used everywhere an Agent-facing
// surface shows e-KYC status - the drawer's status-only section, the
// stepper's e-KYC step, the Org Chart row, and the Dashboard item all read
// from this one place, so the same applicant always shows the same status
// text everywhere. Distinct from describeApplicationStatus's shorter badge
// label ("รอ e-KYC") since this one has room to say the review happens
// centrally, not with the Agent - the whole point of Prompt 138Y-D is that
// the Agent never starts e-KYC themselves, so this copy never implies an
// available action, only a real status. Every real applicationStatus this
// backend can return is covered explicitly, honest per status - never a
// generic "in progress" placeholder for a needs-correction/rejected outcome.
const EKYC_PROCESS_STATUS_LABEL = {
  QuickRegistered: "รอ e-KYC ส่วนกลาง",
  EkycInProgress: "e-KYC กำลังดำเนินการโดยทีมกลาง",
  EkycNeedsCorrection: "ต้องแก้ไขข้อมูลตามผลตรวจ e-KYC",
  EkycApproved: "e-KYC ผ่านแล้ว รอสร้าง Worker Profile",
  EkycRejected: "e-KYC ไม่ผ่าน กรุณาประสานทีมกลาง",
  // P157-R3: was "สร้าง Worker Profile แล้ว", which is the NEXT stage's label,
  // not an e-KYC outcome. The stepper puts this string on its e-KYC step and the
  // profile step's own label directly under it, so an already-profiled applicant
  // rendered the identical line twice in a row - visible on every row click now
  // that the whole row opens this drawer. This map answers "what happened to
  // e-KYC", and for a profiled applicant the honest answer is that it passed.
  WorkerProfileCreated: "e-KYC ผ่านแล้ว",
};
const DEFAULT_EKYC_PROCESS_STATUS_LABEL = "รอ e-KYC ส่วนกลาง";

export function describeEkycProcessStatus(applicationStatus) {
  return EKYC_PROCESS_STATUS_LABEL[applicationStatus] ?? DEFAULT_EKYC_PROCESS_STATUS_LABEL;
}

// Prompt 138Y-B: the drawer's headline copy - a calm, factual restatement
// of a fact the backend already persisted at quick-register time
// (WmxWorkerApplications.AgentId is set the moment the row is created,
// confirmed against the schema/service source during the Prompt 138Y-A
// audit), never a clickable "confirm" action. There is no backend column
// or endpoint for a distinct acceptance *event* (the audit's own gap
// finding) - only ever restate what is already real, never invent a click
// that would imply something just got persisted.
export const LINE_MEMBERSHIP_COPY = {
  heading: "รับเข้าสังกัดแล้ว",
  body: "สมัครผ่าน Invite Card และอยู่ในสายงาน Agent นี้แล้ว",
};

// Prompt 138Y-C: the same already-true fact as LINE_MEMBERSHIP_COPY, but
// short enough to sit as a compact tag on a card/row (the Dashboard item
// and Org Chart row both need this, not the drawer's fuller sentence).
export const LINE_MEMBERSHIP_TAG = "อยู่ในสายงาน";

/**
 * Prompt 138Y-B: the drawer's 5-step status stepper, derived purely from
 * the real applicationStatus (see describeApplicationStatus's own doc
 * comment for why). The first two steps ("สมัครผ่าน Invite Card" /
 * "อยู่ในสายงาน Agent") are unconditionally "done" - both are already-true
 * facts the moment the application row exists (AgentId is set at creation,
 * per the Prompt 138Y-A audit), never a guess or a pending state. "Ready
 * For Match" stays unconditionally "locked" - every response this backend
 * returns today sets stage.readyForMatch to false regardless of
 * ApplicationStatus, and no ReadyForMatch-marking endpoint exists yet.
 */
export function resolveApplicantSteps(applicationStatus) {
  const ekycState = EKYC_STEP_STATE[applicationStatus] ?? "waiting";
  const workerProfileDone = applicationStatus === "WorkerProfileCreated";
  return [
    { key: "invited", label: "สมัครผ่าน Invite Card", state: "done" },
    { key: "lineMembership", label: "อยู่ในสายงาน Agent", state: "done" },
    { key: "ekyc", label: describeEkycProcessStatus(applicationStatus), state: ekycState },
    { key: "workerProfile", label: workerProfileDone ? "สร้าง Worker Profile แล้ว" : "รอสร้าง Worker Profile", state: workerProfileDone ? "done" : "pending" },
    { key: "readyForMatch", label: "Ready For Match", state: "locked" },
  ];
}

// Prompt 138Y-D: the Agent never starts e-KYC themselves - removed the
// EKYC_START_ACTION/resolveEkycStartAction pair Prompt 138W-R5 wired to the
// real POST .../ekyc/start endpoint and Prompt 138Y-B demoted to a
// secondary button. That endpoint is still real and still callable by the
// owning Agent server-side (EkycReviewService.StartEkycAsync), and the
// SuperAdmin-facing Worker Application Inbox keeps its own independent,
// already-disabled placeholder button untouched - this file just no longer
// offers any Agent-facing UI path to trigger it. See
// workerApplicationsApi.js's own startEkyc export, which stays in place
// as an accurate (if currently unused) wrapper for that real endpoint.

// Prompt 138Y-D: the drawer's (and, by extension, every Agent surface's)
// honest "what happens next" pointer - distinct from the e-KYC *status*
// text above (describeEkycProcessStatus), since this answers "what do I,
// the Agent, do about it" rather than "what state is it in." Only the
// WorkerProfileCreated case gets a real navigation target
// (/backoffice/agent/workers, the real Workers list route) - every other
// status is plain text, since there is no real Agent-facing action for
// "track e-KYC" or "coordinate with the central team" to link to.
export function resolveApplicantNextAction(applicationStatus) {
  switch (applicationStatus) {
    case "WorkerProfileCreated":
      return { text: "เปิดหน้า Workers", to: "/backoffice/agent/workers" };
    case "EkycApproved":
      return { text: "รอสร้าง Worker Profile", to: null };
    case "EkycRejected":
    case "EkycNeedsCorrection":
      return { text: "ประสานทีมกลาง / ติดต่อผู้สมัคร", to: null };
    default:
      return { text: "ติดตามสถานะ e-KYC", to: null };
  }
}

// Prompt 140B-R2: a person who Quick Registered through an Agent invite is
// already a real worker in that Agent's line the moment they exist - e-KYC/
// Worker Profile completion/Ready for Match are status readouts on that
// same real person, never conditions for whether they appear at all
// anywhere in the Agent Portal (Dashboard/Workers/Org Chart/Buddy
// Guarantee). This constant/helper pair is the one honest place every one
// of those surfaces reads "is this real person trackable yet" from - device
// binding/mobile tracking data only ever exists once a real job Assignment
// begins onboarding (AssignmentOnboardingService/dbo.WmxAssignmentOnboarding
// - confirmed by reading the backend source, never guessed), so there is no
// real per-worker/per-applicant tracking field to read at this stage for
// either a WorkerApplication or a WorkerProfile record. This is therefore a
// fixed, honest "not started yet" status text - never a computed field,
// never implying tracking silently started, and never gated on Ready for
// Match (a worker becomes trackable once they arrive and bind a device on
// a real assignment, not once matched).
export const TRACKING_NOT_STARTED_NOTE = "รอเปิดแอป / รอผูกเครื่อง / ยังไม่เริ่ม Tracking";

export function describeTrackingStatus() {
  return TRACKING_NOT_STARTED_NOTE;
}
